import {Input} from 'antd';
import {InputProps} from "antd/es/input";
import React, {ChangeEvent, useCallback, useEffect, useState} from 'react';

import {normalizeNumberInput} from '../lib';

type TCustomDecimalInputProps = Omit<InputProps, 'value' | 'onChange'> & {
    value?: number | null;
    onChange?: (value: number | null) => void;
};

/**
 * Компонент для ввода десятичных чисел с запятой в качестве десятичного разделителя.
 *
 * Особенности:
 * - При вводе пользователя происходит нормализация:
 * разрешены только цифры, один знак (+ или -) в начале и одна запятая.
 * - Разделитель в отображении - запятая, в числовом значении - точка (стандарт JavaScript).
 *
 * @template TCustomDecimalInputProps - тип пропсов компонента.
 *
 * @param props - Объект с пропсами.
 * @param props.value - Текущее числовое значение (или null/undefined). При null или undefined поле показывается пустым.
 * @param props.onChange - Callback, вызываемый при изменении значения.
 * @param props.restProps - Все остальные пропсы, поддерживаемые `Input` из Ant Design
 * (placeholder, disabled, autoComplete и т.д.).
 *
 * @returns React-компонент поля ввода.
 */
export const CustomDecimalInput = (props: TCustomDecimalInputProps) => {
    const {
        value,
        onChange,
        ...restProps
    } = props;

    const [inputValue, setInputValue] = useState<string>(
        value?.toString().replace('.', ',') ?? ''
    );


    useEffect(() => {
        if (value === undefined || value === null) {
            setInputValue('');
            return;
        }
        const normalizedValue = value.toString().replace('.', ',');
        if (inputValue !== normalizedValue) {
            setInputValue(normalizedValue);
        }
    }, [value]);

    const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        const targetValue = e.target.value;

        if (!targetValue) {
            setInputValue('');
            onChange?.(null);
            return;
        }

        const normalized = normalizeNumberInput(targetValue);
        setInputValue(normalized);

        if (normalized === '-' || normalized === '+') {
            onChange?.(null);
            return;
        }

        const cleanValue = normalized.endsWith(',')
            ? normalized.slice(0, -1)
            : normalized;

        const numericValue = Number(cleanValue.replace(',', '.'));

        if (!Number.isNaN(numericValue)) {
            onChange?.(numericValue);
        }
    }, [onChange]);
    return (
        <Input
            value={inputValue}
            onChange={handleChange}
            type="text"
            inputMode="decimal"
            {...restProps}
        />
    );
};